import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: infoRef, visibleItems: infoItems } = useStaggerAnimation(3, { threshold: 0.1 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.05 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7g6t2mi0ais34q5lt0g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/e27741aedf4c8db132f8db2dd358f523.jpeg"
          alt="株式会社FD お問い合わせ 訪問介護・障害福祉サービス"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="text-center">
            <p
              className="text-white/70 text-xs font-['Poppins'] tracking-widest mb-3"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              CONTACT
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-white tracking-wide"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              お問い合わせ
            </h1>
            <div
              className="flex items-center justify-center gap-2 mt-4 text-xs text-white/60"
              style={{
                opacity: heroVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.3s",
              }}
            >
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">TOP</Link>
              <span>›</span>
              <span className="text-white/90">お問い合わせ</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#f7fcfd]">
        <div className="max-w-[1240px] mx-auto px-6">
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
            ref={infoRef as React.RefObject<HTMLDivElement>}
          >
            {[
              {
                icon: "ri-phone-line",
                label: "お電話でのお問い合わせ",
                main: "0566-68-6220",
                sub: "ヘルパーステーションみりん",
                note: "FAX: 0566-68-6221",
                href: "tel:0566-68-6220",
              },
              {
                icon: "ri-smartphone-line",
                label: "代表携帯（株式会社FD）",
                main: "080-6952-9336",
                sub: "株式会社FD 代表",
                note: "お急ぎの場合はこちら",
                href: "tel:080-6952-9336",
              },
              {
                icon: "ri-time-line",
                label: "受付時間",
                main: "9:00 〜 18:00",
                sub: "月曜日〜土曜日",
                note: "日曜・祝日はお休みです",
                href: null,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 text-center"
                style={{
                  opacity: infoItems[i] ? 1 : 0,
                  transform: infoItems[i] ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.7s ease ${i * 0.12}s, transform 0.7s ease ${i * 0.12}s`,
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f8fb] mx-auto mb-4">
                  <i className={`${item.icon} text-xl text-[#3ab5c6]`}></i>
                </div>
                <p className="text-xs text-gray-400 mb-2">{item.label}</p>
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-xl font-bold font-['Poppins'] text-gray-800 hover:text-[#3ab5c6] transition-colors cursor-pointer block mb-1"
                  >
                    {item.main}
                  </a>
                ) : (
                  <p className="text-xl font-bold font-['Poppins'] text-gray-800 mb-1">{item.main}</p>
                )}
                <p className="text-xs text-gray-600 mb-0.5">{item.sub}</p>
                <p className="text-xs text-gray-400">{item.note}</p>
              </div>
            ))}
          </div>

          {/* Form */}
          <div
            className="max-w-[760px] mx-auto"
            ref={formRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: formVisible ? 1 : 0,
              transform: formVisible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="text-center mb-10">
              <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-1">FORM</p>
              <h2 className="text-2xl font-bold text-gray-800">メールでのお問い合わせ</h2>
              <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                下記フォームにご記入の上、送信してください。<br className="hidden sm:block" />
                通常2〜3営業日以内にご返信いたします。
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#e8f8fb] rounded-2xl p-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#3ab5c6] mx-auto mb-5">
                  <i className="ri-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">送信が完了しました</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  内容を確認の上、担当者よりご連絡いたします。
                </p>
                <Link
                  to="/"
                  className="inline-block mt-8 bg-[#3ab5c6] text-white text-sm px-8 py-3 rounded-full hover:bg-[#2da0b0] transition-colors cursor-pointer whitespace-nowrap"
                >
                  トップページへ戻る
                </Link>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 md:p-10 space-y-6"
              >
                {/* お問い合わせ種別 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    お問い合わせ種別 <span className="text-rose-500 text-xs ml-1">必須</span>
                  </label>
                  <select
                    name="inquiry_type"
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3ab5c6] transition-colors cursor-pointer bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="サービスについて">サービスについて</option>
                    <option value="料金について">料金について</option>
                    <option value="採用について">採用について</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* 氏名 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-rose-500 text-xs ml-1">必須</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      フリガナ
                    </label>
                    <input
                      type="text"
                      name="furigana"
                      placeholder="ヤマダ タロウ"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                </div>

                {/* メール・電話 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-rose-500 text-xs ml-1">必須</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="090-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                </div>

                {/* 件名 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    件名 <span className="text-rose-500 text-xs ml-1">必須</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="お問い合わせの件名をご入力ください"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                  />
                </div>

                {/* 内容 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    お問い合わせ内容 <span className="text-rose-500 text-xs ml-1">必須</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors resize-none"
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount >= 500 ? "text-rose-500" : "text-gray-400"}`}>
                    {charCount} / 500文字
                  </p>
                </div>

                {/* Privacy */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    name="privacy_agreed"
                    required
                    className="mt-0.5 accent-[#3ab5c6] cursor-pointer"
                  />
                  <label htmlFor="privacy" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                    <a href="#" className="text-[#3ab5c6] hover:underline">プライバシーポリシー</a>に同意の上、送信します。
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#3ab5c6] text-white text-sm font-medium py-4 rounded-full hover:bg-[#2da0b0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "送信中..." : "送信する"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {/* Schema.org ContactPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "お問い合わせ｜株式会社FD",
          "description": "愛知県刈谷市の株式会社FD・ヘルパーステーションみりんへのお問い合わせページ。訪問介護・障害福祉サービスに関するご相談はこちらから。",
          "url": "https://future-diver.jp/contact",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "ヘルパーステーションみりん",
            "telephone": "+81-566-68-6220",
            "faxNumber": "+81-566-68-6221",
            "email": "info@future-diver.jp",
            "address": {
              "@type": "PostalAddress",
              "postalCode": "448-0001",
              "addressRegion": "愛知県",
              "addressLocality": "刈谷市",
              "streetAddress": "井ケ谷町下前田6番地3 コーポ龍美218号室"
            },
            "openingHours": "Mo-Sa 09:00-18:00",
            "areaServed": "愛知県刈谷市および周辺地域"
          }
        })}
      </script>
    </div>
  );
}
